import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ContentPageHeader, MetricGrid, SummaryMetricCard, TableLink } from '@/components/global';
import { CollapsibleSection } from '@/components/global/CollapsibleSection';
import { DateRangePicker, useDateRange } from '@/components/finance';
import { DollarSign, TrendingUp, Percent, CreditCard, Download } from 'lucide-react';
import { downloadCsv } from '@/lib/csvExport';

interface DashboardSummary {
  period: { startDate: string; endDate: string };
  revenue: {
    grossSales: number;
    refunds: number;
    netRevenue: number;
    trend: number | null;
  };
  costs: {
    cogs: number;
    venueCommissions: number;
    warehouseCosts: number;
    refillCosts: number;
    total: number;
  };
  profit: {
    grossProfit: number;
    operatingProfit: number;
    marginPercent: number;
  };
  commissions: {
    pending: number;
    approved: number;
    paid: number;
    totalDue: number;
  };
}

interface HubPnL {
  hubId: string;
  hubName: string;
  totalRevenue: number;
  totalProductCost: number;
  totalGrossProfit: number;
  totalVenueCommission: number;
  hubOperatingCost: number;
  netProfit: number;
  marginPercent: number;
}

interface OrgPnL {
  hubs: HubPnL[];
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`;
}

export default function FinanceDashboard() {
  const { dateRange, setDateRange } = useDateRange('MTD');
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [orgPnl, setOrgPnl] = useState<OrgPnL | null>(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        startDate: dateRange.startDate.toISOString(),
        endDate: dateRange.endDate.toISOString(),
      });

      const [summaryRes, pnlRes] = await Promise.all([
        apiFetch(`/api/v1/finance/dashboard?${params}`),
        apiFetch(`/api/v1/finance/pnl?${params}`),
      ]);

      if (summaryRes.ok) {
        const data = await summaryRes.json();
        setSummary(data);
      }

      if (pnlRes.ok) {
        const data = await pnlRes.json();
        setOrgPnl(data);
      }
    } catch (error) {
      console.error('Error loading finance data:', error);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  function exportCsv() {
    if (!orgPnl || orgPnl.hubs.length === 0) return;
    downloadCsv(
      `finance-pnl-by-hub-${new Date().toISOString().slice(0, 10)}.csv`,
      [
        'Hub',
        'Revenue (GBP)',
        'COGS (GBP)',
        'Gross profit (GBP)',
        'Commission (GBP)',
        'Operating costs (GBP)',
        'Net profit (GBP)',
        'Margin %',
      ],
      orgPnl.hubs.map((hub) => [
        hub.hubName,
        hub.totalRevenue.toFixed(2),
        hub.totalProductCost.toFixed(2),
        hub.totalGrossProfit.toFixed(2),
        hub.totalVenueCommission.toFixed(2),
        hub.hubOperatingCost.toFixed(2),
        hub.netProfit.toFixed(2),
        hub.marginPercent.toFixed(1),
      ])
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="space-y-6">
        <ContentPageHeader
          title="Finance"
          actions={
            <>
              <DateRangePicker
                value={dateRange}
                onChange={setDateRange}
                className="w-full sm:w-auto"
              />
              <Button
                variant="outline"
                onClick={exportCsv}
                disabled={loading || !orgPnl || orgPnl.hubs.length === 0}
                className="w-full sm:w-auto"
              >
                <Download className="mr-2 h-4 w-4" />
                Export
              </Button>
            </>
          }
        />

        <MetricGrid className="mb-6">
          <SummaryMetricCard
            label="Net Revenue"
            value={summary ? formatCurrency(summary.revenue.netRevenue) : '-'}
            icon={DollarSign}
            trend={summary?.revenue.trend ? { value: summary.revenue.trend, direction: summary.revenue.trend >= 0 ? 'up' : 'down' } : undefined}
            isLoading={loading}
          />
          <SummaryMetricCard
            label="Gross Profit"
            value={summary ? formatCurrency(summary.profit.grossProfit) : '-'}
            icon={TrendingUp}
            isLoading={loading}
          />
          <SummaryMetricCard
            label="Operating Margin"
            value={summary ? formatPercent(summary.profit.marginPercent) : '-'}
            icon={Percent}
            isLoading={loading}
          />
          <SummaryMetricCard
            label="Commission Due"
            value={summary ? formatCurrency(summary.commissions.totalDue) : '-'}
            icon={CreditCard}
            isLoading={loading}
          />
        </MetricGrid>

        <CollapsibleSection title="P&L by Hub" defaultOpen className="mb-6">
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Hub</TableHead>
                  <TableHead className="text-right">Revenue</TableHead>
                  <TableHead className="text-right">COGS</TableHead>
                  <TableHead className="text-right">Gross Profit</TableHead>
                  <TableHead className="text-right">Commission</TableHead>
                  <TableHead className="text-right">Op. Costs</TableHead>
                  <TableHead className="text-right">Net Profit</TableHead>
                  <TableHead className="text-right">Margin</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : orgPnl?.hubs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No data available
                    </TableCell>
                  </TableRow>
                ) : (
                  orgPnl?.hubs.map((hub) => (
                    <TableRow key={hub.hubId}>
                      <TableCell>
                        <TableLink to={`/finance/hubs/${hub.hubId}/pnl`}>
                          {hub.hubName}
                        </TableLink>
                      </TableCell>
                      <TableCell className="text-right">{formatCurrency(hub.totalRevenue)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(hub.totalProductCost)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(hub.totalGrossProfit)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(hub.totalVenueCommission)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(hub.hubOperatingCost)}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(hub.netProfit)}</TableCell>
                      <TableCell className="text-right">{formatPercent(hub.marginPercent)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </CollapsibleSection>

        <CollapsibleSection title="Cost Breakdown" defaultOpen className="mb-6">
          <Card>
            <CardContent className="pt-6">
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">Loading...</div>
              ) : summary ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold">{formatCurrency(summary.costs.cogs)}</div>
                    <div className="text-sm text-muted-foreground">Product Cost (COGS)</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold">{formatCurrency(summary.costs.venueCommissions)}</div>
                    <div className="text-sm text-muted-foreground">Venue Commissions</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold">{formatCurrency(summary.costs.warehouseCosts)}</div>
                    <div className="text-sm text-muted-foreground">Warehouse Costs</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold">{formatCurrency(summary.costs.refillCosts)}</div>
                    <div className="text-sm text-muted-foreground">Refill Costs</div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">No data available</div>
              )}
            </CardContent>
          </Card>
        </CollapsibleSection>

        <CollapsibleSection title="Commission Summary" defaultOpen>
          <Card>
            <CardContent className="pt-6">
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">Loading...</div>
              ) : summary ? (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-yellow-600">{formatCurrency(summary.commissions.pending)}</div>
                    <div className="text-sm text-muted-foreground">Pending</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{formatCurrency(summary.commissions.approved)}</div>
                    <div className="text-sm text-muted-foreground">Approved</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">{formatCurrency(summary.commissions.paid)}</div>
                    <div className="text-sm text-muted-foreground">Paid</div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">No data available</div>
              )}
            </CardContent>
          </Card>
          <div className="mt-4">
            <Link to="/finance/commissions">
              <Button variant="outline">View All Commissions</Button>
            </Link>
          </div>
        </CollapsibleSection>
      </div>
    </div>
  );
}
